import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { getAbbreviationsByShort } from "@/api/senses";

/** Sense-grouped review: показывает все sense'ы одной аббревиатуры
 *  side-by-side. Полезно для polysemic-аббревиатур (AD = Alzheimer's
 *  Disease vs atopic dermatitis). */
export default function SenseGroupedReview() {
  const { short = "" } = useParams<{ short: string }>();

  const query = useQuery({
    queryKey: ["abbrev-by-short", short],
    queryFn: async () => {
      const r = await getAbbreviationsByShort(short);
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    enabled: Boolean(short),
  });

  if (query.isLoading) {
    return <div className="mt-empty">Загружаем…</div>;
  }
  if (query.isError) {
    return (
      <div className="mt-empty mt-empty--error">
        {(query.error as Error).message}
      </div>
    );
  }
  const data = query.data!;

  return (
    <div className="rb-shell">
      <header className="mt-page__head" style={{ marginBottom: "var(--sp-3)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Link to="/admin/review" className="mt-btn mt-btn--sm mt-btn--ghost">
            ‹ К очереди
          </Link>
          <h1 style={{ margin: 0 }}>
            Аббревиатура{" "}
            <span className="mt-mono" style={{ color: "var(--ink)" }}>
              {short}
            </span>
          </h1>
          {data.is_polysemic && (
            <span className="mt-badge mt-badge--warning mt-badge--no-dot">
              многозначная
            </span>
          )}
          <span className="mt-toolbar__sep" style={{ flex: 1 }} />
          <span className="mt-text-tight mt-muted">
            {data.summary.approved_count} принято · {data.summary.candidate_count} кандидатов
          </span>
        </div>
      </header>

      {data.abbreviations.length > 0 && (
        <div className="rb-card">
          <div className="rb-card__head">
            <h2 className="rb-card__title">Принятые расшифровки</h2>
            <span className="mt-badge mt-badge--success mt-badge--no-dot">
              {data.abbreviations.length}
            </span>
          </div>
          <div className="rb-card__senses">
            {data.abbreviations.map((row) => (
              <div key={row.abbreviation_id} className="rb-sense">
                <div className="rb-sense__head">
                  <span className="mt-mono">
                    <strong>{row.source_abbreviation}</strong>
                  </span>
                  <span className="rl-arrow">→</span>
                  <span>{row.target_expansion}</span>
                </div>
                <div className="rb-sense__pair">{row.source_expansion}</div>
                <div className="rb-sense__meta">
                  {row.domain && <span>{row.domain} · </span>}
                  <span>{row.status}</span>
                  {row.confidence != null && (
                    <>
                      {" · "}
                      <span className="mt-mono">{row.confidence.toFixed(2)}</span>
                    </>
                  )}
                </div>
                {row.notes && (
                  <div
                    className="mt-text-tight mt-muted"
                    style={{ fontSize: "var(--fs-xs)" }}
                  >
                    {row.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.candidates.length > 0 && (
        <div className="rb-card">
          <div className="rb-card__head">
            <h2 className="rb-card__title">Кандидаты на рассмотрение</h2>
            <span className="mt-badge mt-badge--warning mt-badge--no-dot">
              {data.candidates.length}
            </span>
          </div>
          <div className="rb-card__senses">
            {data.candidates.map((row) => (
              <div key={row.candidate_id} className="rb-sense">
                <div className="rb-sense__head">
                  <span className="mt-mono">
                    <strong>{row.abbreviation}</strong>
                  </span>
                  <span className="rl-arrow">→</span>
                  <span>{row.definition}</span>
                </div>
                <div className="rb-sense__meta">
                  {row.source_name && <span>{row.source_name} · </span>}
                  <span>встречено {row.raw_occurrence_count} раз</span>
                  {row.is_ambiguous && (
                    <>
                      {" · "}
                      <span className="rl-amb" title="ambiguous">±</span>
                    </>
                  )}
                  {row.confidence != null && (
                    <>
                      {" · "}
                      <span className="mt-mono">{row.confidence.toFixed(2)}</span>
                    </>
                  )}
                </div>
                {row.flags.length > 0 && (
                  <div
                    className="mt-text-tight mt-muted"
                    style={{ fontSize: "var(--fs-xs)" }}
                  >
                    {row.flags.join(", ")}
                  </div>
                )}
                {row.reviewer_note && (
                  <div
                    className="mt-text-tight"
                    style={{ fontSize: "var(--fs-xs)", color: "var(--blue-700)" }}
                  >
                    📝 {row.reviewer_note}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.abbreviations.length === 0 && data.candidates.length === 0 && (
        <div className="mt-empty">Нет данных по «{short}».</div>
      )}
    </div>
  );
}
